import { useEffect, useMemo, useState } from "react";
import { Archive, FileText, Loader2, RefreshCw } from "lucide-react";
import {
  getShipmentCase,
  listShipmentCases,
} from "../../services/compliance-api";
import type {
  DocumentHubItem,
  ShipmentCaseDetail,
  ShipmentCaseItem,
} from "../../types/compliance";

type ShipmentCasePickerProps = {
  selectedCaseId: string;
  selectedWorkflowId?: string;
  onSelectCase: (item: ShipmentCaseItem, detail: ShipmentCaseDetail) => void;
  onSelectWorkflow?: (
    document: DocumentHubItem,
    item: ShipmentCaseItem,
    detail: ShipmentCaseDetail,
  ) => void;
};

function humanize(value: string) {
  return value.replace(/_/g, " ");
}

export default function ShipmentCasePicker({
  selectedCaseId,
  selectedWorkflowId,
  onSelectCase,
  onSelectWorkflow,
}: ShipmentCasePickerProps) {
  const [cases, setCases] = useState<ShipmentCaseItem[]>([]);
  const [detail, setDetail] = useState<ShipmentCaseDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void loadCases();
  }, []);

  useEffect(() => {
    if (!selectedCaseId) {
      setDetail(null);
      return;
    }
    void loadDetail(selectedCaseId);
  }, [selectedCaseId]);

  async function loadCases() {
    setLoading(true);
    setError(null);
    try {
      const response = await listShipmentCases();
      setCases(response.items);
      if (!selectedCaseId && response.items[0]) {
        const nextDetail = await getShipmentCase(response.items[0].case_id);
        setDetail(nextDetail);
        onSelectCase(response.items[0], nextDetail);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to load shipment cases");
    } finally {
      setLoading(false);
    }
  }

  async function loadDetail(caseId: string) {
    setError(null);
    try {
      const nextDetail = await getShipmentCase(caseId);
      setDetail(nextDetail);
    } catch (err) {
      setDetail(null);
      setError(err instanceof Error ? err.message : "Unable to load case detail");
    }
  }

  async function handleSelect(caseId: string) {
    const item = cases.find((caseItem) => caseItem.case_id === caseId);
    if (!item) return;
    setError(null);
    try {
      const nextDetail = await getShipmentCase(caseId);
      setDetail(nextDetail);
      onSelectCase(item, nextDetail);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to select case");
    }
  }

  const selectedCase = useMemo(
    () => cases.find((item) => item.case_id === selectedCaseId),
    [cases, selectedCaseId],
  );

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Archive size={16} className="text-blue-700" />
          <h2 className="text-sm font-semibold text-gray-900">
            Shipment Case Context
          </h2>
        </div>
        <button
          type="button"
          onClick={() => void loadCases()}
          className="inline-flex items-center gap-1 text-xs font-medium text-blue-700 hover:text-blue-900"
        >
          <RefreshCw size={12} />
          Refresh
        </button>
      </div>

      {loading ? (
        <div className="py-4 text-sm text-gray-500">
          <Loader2 size={16} className="mr-2 inline-block animate-spin" />
          Loading cases...
        </div>
      ) : cases.length === 0 ? (
        <p className="py-2 text-sm text-gray-500">
          No shipment cases are available yet. Analyze and save a document batch first.
        </p>
      ) : (
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]">
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">
              Active Case
            </label>
            <select
              value={selectedCaseId}
              onChange={(event) => void handleSelect(event.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            >
              {cases.map((item) => (
                <option key={item.case_id} value={item.case_id}>
                  {item.shipment_reference || item.case_id} - {humanize(item.status)}
                </option>
              ))}
            </select>
            {selectedCase && (
              <div className="mt-2 rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-600">
                <p className="truncate font-mono text-gray-800">{selectedCase.case_id}</p>
                <p className="mt-1">
                  {selectedCase.document_count} docs · {selectedCase.risk_count} risks · {selectedCase.mismatch_count} mismatches
                </p>
              </div>
            )}
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">
              Document Workflow
            </label>
            {detail?.documents.length ? (
              <div className="flex flex-wrap gap-2">
                {detail.documents.map((document) => {
                  const selected = document.workflow_id === selectedWorkflowId;
                  return (
                    <button
                      key={document.document_id}
                      type="button"
                      onClick={() =>
                        selectedCase &&
                        onSelectWorkflow?.(document, selectedCase, detail)
                      }
                      className={`inline-flex items-center gap-1 rounded-lg border px-3 py-2 text-xs font-medium transition ${
                        selected
                          ? "border-blue-600 bg-blue-50 text-blue-700"
                          : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      <FileText size={12} />
                      {humanize(document.document_type)}
                    </button>
                  );
                })}
              </div>
            ) : (
              <p className="rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-500">
                This case has no stored document workflows yet.
              </p>
            )}
          </div>
        </div>
      )}

      {error && <p className="mt-3 text-xs text-red-600">{error}</p>}
    </div>
  );
}
